// Issue #359: Scheduled Test Data management page (Testdaten hochladen)
import React, { useState, useEffect, useCallback } from 'react';
import { betaApi } from '../../services/api';
import CreateTestDataModal from './CreateTestDataModal';

function ScheduledTestData() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filters, setFilters] = useState({
    content_type: '',
    university: '',
  });
  const [universities, setUniversities] = useState([]);

  // Beta is only available for these universities
  const BETA_UNIVERSITIES = ['TU Hamburg', 'HAW Kiel'];

  const perPage = 20;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        per_page: perPage,
        ...(filters.content_type && { content_type: filters.content_type }),
        ...(filters.university && { university: filters.university }),
      };
      const response = await betaApi.listScheduled(params);
      setItems(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching scheduled test data:', err);
      setError('Fehler beim Laden der Testdaten');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  const fetchUniversities = useCallback(async () => {
    try {
      const response = await betaApi.getUniversities();
      setUniversities(response.data || []);
    } catch (err) {
      console.error('Error fetching universities:', err);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchUniversities();
  }, [fetchUniversities]);

  const handleDelete = async (id) => {
    if (!window.confirm('Sicher, dass diese Testdaten gelöscht werden sollen?')) {
      return;
    }
    try {
      await betaApi.delete(id);
      fetchData();
    } catch (err) {
      console.error('Error deleting test data:', err);
      alert('Fehler beim Löschen');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowCreateModal(true);
  };

  const handleCreateClose = () => {
    setShowCreateModal(false);
    setEditingItem(null);
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    setEditingItem(null);
    fetchData();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getContentTypeLabel = (type) => {
    const labels = {
      post: 'Beitrag',
      marketplace: 'Marktplatz',
      comment: 'Kommentar',
      course_review: 'Kursbewertung',
    };
    return labels[type] || type;
  };

  const truncateContent = (content, maxLength = 100) => {
    if (!content) return '-';
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Geplante Testdaten</h1>
          <p className="page-subtitle">{total} Einträge insgesamt</p>
        </div>
        <button
          className="primary-button"
          onClick={() => setShowCreateModal(true)}
        >
          + Erstellen
        </button>
      </div>

      <div className="filters-bar">
        <select
          className="filter-select"
          value={filters.content_type}
          onChange={(e) => {
            setFilters({ ...filters, content_type: e.target.value });
            setPage(1);
          }}
        >
          <option value="">Alle Inhaltstypen</option>
          <option value="post">Beitrag</option>
          <option value="marketplace">Marktplatz</option>
          <option value="comment">Kommentar</option>
          <option value="course_review">Kursbewertung</option>
        </select>

        <select
          className="filter-select"
          value={filters.university}
          onChange={(e) => {
            setFilters({ ...filters, university: e.target.value });
            setPage(1);
          }}
        >
          <option value="">Alle Universitäten</option>
          {universities.map((uni) => (
            <option key={uni} value={uni}>{uni}</option>
          ))}
        </select>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <p>Keine geplanten Testdaten vorhanden.</p>
          <p>Klicken Sie auf "Erstellen", um neue Testdaten zu planen.</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Typ</th>
                  <th>Universität</th>
                  <th>Inhalt</th>
                  <th>Geplante Uploadzeit</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <span className={`type-badge ${item.content_type}`}>
                        {getContentTypeLabel(item.content_type)}
                      </span>
                    </td>
                    <td>{item.university}</td>
                    <td className="content-cell">
                      {item.title && <strong>{item.title}: </strong>}
                      {truncateContent(item.content)}
                    </td>
                    <td>{formatDate(item.scheduled_at)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="action-btn edit"
                          onClick={() => handleEdit(item)}
                          title="Bearbeiten"
                        >
                          ✏️
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={() => handleDelete(item.id)}
                          title="Löschen"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                ← Zurück
              </button>
              <span className="pagination-info">
                Seite {page} von {totalPages}
              </span>
              <button
                className="pagination-btn"
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Weiter →
              </button>
            </div>
          )}
        </>
      )}

      {showCreateModal && (
        <CreateTestDataModal
          item={editingItem}
          universities={universities.filter(u => BETA_UNIVERSITIES.includes(u))}
          onClose={handleCreateClose}
          onSuccess={handleCreateSuccess}
        />
      )}
    </div>
  );
}

export default ScheduledTestData;
